import { useState } from "react";
import { ScheduleCard } from "./types";
import styles from "./styles.module.scss";
import dayjs from "dayjs";
import { formatNumber } from "@/uiCore";
import { Tooltip } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import classNames from "classnames";
import { getIcon, parseRecommendationReasoning } from "./helper";
import { default as TrashIcon } from "@/assets/icons/trash.svg?react";
import { default as WriteIcon } from "@/assets/icons/write_icon.svg?react";
import { Badge } from "@altimateai/lego";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { rejectScheduleEvent } from "@/helpers/apis";
import { GET_WAREHOUSE_SCHEDULED_EVENTS } from "../../constants";
import { toast } from "@altimateai/lego";
import AddScheduleForm from "../schedulerForms/AddScheduleForm";
import { getInitialValues } from "../schedulerForms/formConfig";
import { useUsers } from "@/helpers/hooks";
import { ScheduleResponse } from "../../types";
import { WarehouseTracking } from "@/lib/tracking/warehouse";

interface ViewScheduleContentProps {
  event: ScheduleCard;
  onClose: () => void;
  warehouseName: string;
  defaultWarehouseSize?: string;
  instanceId: string;
}

export const ViewScheduleContent: React.FC<ViewScheduleContentProps> = ({
  event,
  onClose,
  warehouseName,
  instanceId,
  defaultWarehouseSize,
}) => {
  const { currency } = useAppState();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const { getById } = useUsers();
  const formattedTime = `${dayjs(event.start).format("MMM D, h:mm A")} - ${dayjs(event.end).format("h:mm A")}`;
  const isCustom = event.type === "custom";
  const isExpired = dayjs(event.end).isBefore(dayjs());
  const isApplied = event.status === "approved";

  const { mutate: rejectSchedule } = useMutation({
    mutationFn: () => rejectScheduleEvent(event?.id, { reason: "rejected" }),
    onSuccess: () => {
      WarehouseTracking.scheduleDeleted(warehouseName, event.id);
      queryClient.setQueryData<ScheduleResponse[]>([GET_WAREHOUSE_SCHEDULED_EVENTS], oldData => {
        if (!oldData) return [];
        return oldData.filter(item => item.schedule_id.toString() !== event.id);
      });
      onClose();
      toast({ description: "Schedule deleted successfully" });
    },
    onError: error => {
      toast({
        description: (error as Error | undefined)?.message || "Failed to delete schedule",
        variant: "destructive",
      });
    },
  });

  const getHeaderStyle = () => {
    if (isExpired) {
      return { backgroundColor: "#EEEFF2" };
    }
    if (isApplied) {
      return { backgroundColor: isCustom ? "#E38E00" : "#247EFE", color: "white" };
    }
    return {};
  };

  if (isEditing) {
    return (
      <AddScheduleForm
        isEdit
        scheduleId={event.id}
        instanceId={instanceId}
        warehouseSize={event.sizeTo}
        defaultWarehouseSize={defaultWarehouseSize}
        initialValues={getInitialValues(event)}
        warehouseName={warehouseName}
        handleClose={onClose}
        handleCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className={styles.viewScheduleContent}>
      <div
        className={classNames(styles.header, {
          [styles.recommended]: !isCustom && !isExpired && !isApplied,
          [styles.custom]: isCustom && !isExpired && !isApplied,
          [styles.expired]: isExpired,
        })}
        style={getHeaderStyle()}
      >
        <div className={styles.headerLeft}>
          {getIcon({
            type: event.type,
            change: event.change ?? "no_change",
          })}
          <div className={styles.title}>
            {isExpired
              ? `${isCustom ? "Custom" : "Recommended"} (Expired)`
              : `${isCustom ? "Custom" : "Recommended"}${isApplied ? " (Applied)" : ""}`}
          </div>
        </div>
        <div className={styles.headerRight}>
          {!isExpired && (
            <>
              <Tooltip title="Delete Schedule">
                <button
                  className={classNames(styles.actionButton, styles.deleteButton)}
                  onClick={() => rejectSchedule()}
                >
                  <TrashIcon />
                </button>
              </Tooltip>
              {isCustom && (
                <Tooltip title="Edit Schedule">
                  <button
                    className={classNames(styles.actionButton, styles.editButton)}
                    onClick={() => setIsEditing(true)}
                  >
                    <WriteIcon />
                  </button>
                </Tooltip>
              )}
            </>
          )}
          <button
            className={classNames(styles.actionButton, {
              [styles.whiteButton]: isApplied && !isExpired,
            })}
            onClick={onClose}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.row}>
          <div className={styles.label}>Schedule Time</div>
          <div className={classNames(styles.value, styles.scheduleTime)}>{formattedTime}</div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>WH Size</div>
          <div className={classNames(styles.value, styles.whSize)}>
            {event.sizeTo || event.sizeFrom}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Money Savings</div>
          <div className={styles.value}>
            {event.savings && event.type !== "custom" ? (
              <Badge variant="default" className={styles.savingsBadgePill}>
                {formatNumber(parseFloat(event.savings), { currency })}
              </Badge>
            ) : (
              "N/A"
            )}
          </div>
        </div>

        {isCustom && event.created_by && (
          <div className={styles.row}>
            <div className={styles.label}>Created By</div>
            <div className={styles.value}>
              {getById(event.created_by)?.first_name} {getById(event.created_by)?.last_name}
            </div>
          </div>
        )}

        {!isCustom && event.reasoning && event.reasoning.length > 0 && (
          <div className={styles.row}>
            <div className={styles.label}>Reasoning</div>
            <div className={styles.value}>
              <ul className={styles.impactList}>
                {parseRecommendationReasoning(event.reasoning as string[]).map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
